import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, Pause, Play, RotateCcw, X, FastForward, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_CHIP = {
    active: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    paused: "bg-amber-50 text-amber-700 ring-amber-100",
    cancelled: "bg-zinc-100 text-zinc-500 ring-zinc-200",
};

const SubscriptionDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [qty, setQty] = useState(1);
    const [shippingAddrId, setShippingAddrId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const [{ data: s }, { data: addrs }] = await Promise.all([
                api.get(`/subscriptions/${id}`),
                api.get("/addresses"),
            ]);
            setSub(s);
            setQty(s.qty);
            setShippingAddrId(s.shipping_address_id);
            setAddresses(addrs);
        } catch (e) {
            toast.error("Could not load subscription");
            navigate("/subscriptions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line

    const action = async (path, opts = {}) => {
        setBusy(true);
        try {
            if (path === "refill-now") {
                const { data } = await api.post(`/subscriptions/${id}/refill-now`);
                toast.success(`Order ${data.order_no} created — opening payment...`);
                setTimeout(() => { window.location.href = data.redirect_url; }, 700);
                return;
            }
            await api.post(`/subscriptions/${id}/${path}`);
            toast.success(opts.successMsg || "Updated");
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Action failed";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const saveChanges = async () => {
        if (!sub) return;
        if (saving) return;
        if (qty === sub.qty && shippingAddrId === sub.shipping_address_id) return;
        setSaving(true);
        try {
            await api.patch(`/subscriptions/${id}`, {
                qty: parseInt(qty, 10),
                shipping_address_id: shippingAddrId,
            });
            toast.success("Subscription updated");
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Update failed";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="h-32 skeleton rounded-3xl" />
                <div className="h-64 skeleton rounded-3xl" />
            </div>
        );
    }
    if (!sub) return null;

    const p = sub.product;
    const addr = sub.shipping_address;
    const lineTotal = (sub.price_at_creation || 0) * (sub.qty || 1);
    const dirty = qty !== sub.qty || shippingAddrId !== sub.shipping_address_id;
    const editable = sub.status !== "cancelled";

    return (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="subscription-detail-page">
            <div className="space-y-6">
                <Link to="/subscriptions" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> All subscriptions
                </Link>

                <section className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                    <div className="flex flex-wrap items-start gap-5">
                        <Link to={p ? `/products/${p.id}` : "#"} className="shrink-0">
                            <img
                                src={p?.image || "/pills-logo.jpg"}
                                alt={p?.name}
                                className="h-28 w-28 rounded-2xl object-cover ring-1 ring-zinc-200"
                            />
                        </Link>
                        <div className="min-w-0 flex-1">
                            <p className="label-uppercase">{p?.brand || "PILLS"}</p>
                            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">{p?.name || "Subscription"}</h2>
                            <p className="mt-1 text-sm text-zinc-500">{p?.pack_size}</p>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase ring-1 ${STATUS_CHIP[sub.status]}`} data-testid="sub-detail-status">
                                    {sub.status}
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-medium text-brand-700">
                                    <CalendarClock className="h-3 w-3" /> Monthly
                                </span>
                                {sub.prescription && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                                        <ShieldCheck className="h-3 w-3" /> Rx attached
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-semibold tracking-tight text-zinc-900">{inr(lineTotal)}</p>
                            <p className="text-[10px] uppercase tracking-wide text-zinc-400">per refill</p>
                        </div>
                    </div>
                </section>

                <section className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                    <p className="label-uppercase">Refill schedule</p>
                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Next refill</h3>
                    <p className="mt-1 text-sm text-zinc-500">
                        {sub.status === "active" && <>Reminder will go out on <strong className="text-zinc-900">{fmtDate(sub.next_refill_date)}</strong>.</>}
                        {sub.status === "paused" && <>Subscription is paused. Resume to receive reminders again.</>}
                        {sub.status === "cancelled" && <>This subscription has been cancelled.</>}
                    </p>
                    <p className="mt-2 text-xs text-zinc-500">Last refill: {fmtDate(sub.last_refill_date)}</p>
                </section>

                {editable && (
                    <section className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="sub-detail-edit">
                        <p className="label-uppercase">Manage</p>
                        <h3 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Refill preferences</h3>
                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Quantity per refill</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={qty}
                                    onChange={(e) => setQty(Math.max(1, Math.min(99, parseInt(e.target.value || "1", 10))))}
                                    className="input-clean mt-2"
                                    data-testid="sub-detail-qty-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Shipping address</label>
                                <select
                                    value={shippingAddrId || ""}
                                    onChange={(e) => setShippingAddrId(e.target.value)}
                                    className="input-clean mt-2"
                                    data-testid="sub-detail-address-select"
                                >
                                    {addresses.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {`${a.label} — ${a.city}, ${a.pincode}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <button
                            disabled={!dirty || saving}
                            onClick={saveChanges}
                            className="btn-brand mt-5"
                            data-testid="sub-detail-save-btn"
                        >
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                    </section>
                )}
            </div>

            <aside className="space-y-4 self-start">
                <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                    <p className="label-uppercase">Actions</p>
                    <div className="mt-4 flex flex-col gap-2">
                        {sub.status === "active" && (
                            <>
                                <button disabled={busy} onClick={() => action("refill-now")} className="btn-brand w-full" data-testid="sub-detail-refill-btn">
                                    <RotateCcw className="h-4 w-4" /> Refill now
                                </button>
                                <button disabled={busy} onClick={() => action("skip", { successMsg: "Refill skipped" })} className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-200 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50" data-testid="sub-detail-skip-btn">
                                    <FastForward className="h-4 w-4" /> Skip next refill
                                </button>
                                <button disabled={busy} onClick={() => action("pause", { successMsg: "Subscription paused" })} className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-200 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50" data-testid="sub-detail-pause-btn">
                                    <Pause className="h-4 w-4" /> Pause subscription
                                </button>
                            </>
                        )}
                        {sub.status === "paused" && (
                            <button disabled={busy} onClick={() => action("resume", { successMsg: "Subscription resumed" })} className="btn-brand w-full" data-testid="sub-detail-resume-btn">
                                <Play className="h-4 w-4" /> Resume
                            </button>
                        )}
                        {sub.status !== "cancelled" && (
                            <button
                                disabled={busy}
                                onClick={() => {
                                    if (!window.confirm("Cancel this subscription? This cannot be undone.")) return;
                                    action("cancel", { successMsg: "Subscription cancelled" });
                                }}
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-rose-100 px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                                data-testid="sub-detail-cancel-btn"
                            >
                                <X className="h-4 w-4" /> Cancel subscription
                            </button>
                        )}
                    </div>
                </div>

                {addr && (
                    <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="sub-detail-address-card">
                        <p className="label-uppercase">Shipping to</p>
                        <div className="mt-3 flex items-start gap-3">
                            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-brand-700"><MapPin className="h-4 w-4" /></span>
                            <div className="text-sm text-zinc-700">
                                <p className="font-medium text-zinc-900">{addr.label}</p>
                                <p className="mt-1 text-xs">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                                <p className="text-xs">{addr.city}, {addr.state} - {addr.pincode}</p>
                                <p className="text-xs text-zinc-500">{addr.phone}</p>
                            </div>
                        </div>
                    </div>
                )}
            </aside>
        </div>
    );
};

const SubscriptionDetail = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <SubscriptionDetailContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default SubscriptionDetail;
